import React, { useState, useEffect } from "react";
import { Button, Input } from "antd";
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { showErrorMessage } from "./messages";
import { pageNotExistValidationError, pageNumberValidationError } from "./errorConstants";

const CustomPagination = ({ pagination, onPageChange, groupSize = 3 }) => {
    const { current, pageSize, total } = pagination;

    const [currentPage, setCurrentPage] = useState(current || 1);
    const [currentPageSize, setCurrentPageSize] = useState(pageSize || 10);
    const [inputValue, setInputValue] = useState();

    const totalPages = pagination && Math.ceil(pagination?.total / pagination?.pageSize);
    const currentGroup = Math.floor((currentPage - 1) / groupSize);
    const startPage = currentGroup * groupSize + 1;
    const endPage = Math.min(startPage + groupSize - 1, totalPages);

    useEffect(() => {
        pagination && pagination?.current && setInputValue(current);
    }, [current]);

    useEffect(() => {
        setCurrentPage(current || 1);
    }, [current]);

    useEffect(() => {
        setCurrentPageSize(pageSize || 10);
    }, [pageSize]);

    const handlePageClick = (page) => {
        setCurrentPage(page);
        if (onPageChange) onPageChange(page, currentPageSize);
    };

    const handleNextGroup = () => {
        if (currentPage < totalPages) {
            handlePageClick(currentPage + 1);
        }
    };

    const handlePrevGroup = () => {
        if (currentPage > 1) {
            handlePageClick(currentPage - 1);
        }
    };

    const handleInputChange = (event) => {
        setInputValue(event.target.value);
    };

    const handleSubmit = () => {
        if (!inputValue || inputValue?.toString().trim() === "") {
            showErrorMessage(pageNumberValidationError)
        } else if (inputValue > totalPages || inputValue < 1) {
            const errMessage = pageNotExistValidationError(inputValue);
            showErrorMessage(errMessage);
        } else {
            onPageChange(parseInt(inputValue, 10), pageSize);
        }
    }

    const baseBtnStyle = "flex items-center justify-center !border-none rounded-full w-10 h-10 transition-all duration-200 focus:!outline-none focus:!shadow-none active:!text-black";

    return (
        <div className="flex justify-end items-center py-[0.75rem] px-[1rem] border-t-[0.0625rem] border-t-[#E8E8E8] gap-[1.5rem]">

            <div className="flex items-center justify-center gap-[0.75rem]">
                {/* Prev Button */}
                <Button
                    className={`${baseBtnStyle} !text-[#747474] hover:!bg-[#fef6f7] hover:!text-[#910e11] disabled:!opacity-40 disabled:cursor-not-allowed disabled:hover:!bg-transparent disabled:hover:!text-[#747474]`}
                    onClick={handlePrevGroup}
                    disabled={currentPage === 1}
                >
                    <LeftOutlined />
                </Button>

                <div className="flex gap-[0.75rem]">
                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                        const page = startPage + i;
                        const isActive = currentPage === page;
                        return (
                            <Button
                                key={page}
                                onClick={() => handlePageClick(page)}
                               
                                className={`${baseBtnStyle} ${
                                    isActive 
                                   ? "!bg-[#7f1d1d] !text-white hover:!bg-[#7f1d1d] hover:!text-white" 
                                   : "!text-[#747474] !bg-transparent hover:!text-[#910e11] hover:!bg-[#fef6f7]"
                                }`}
                            >
                                {page}
                            </Button>
                        );
                    })}
                </div>

                {/* Next Button */}
                <Button
                  className={`${baseBtnStyle} !text-[#747474] hover:!bg-[#fef6f7] hover:!text-[#910e11] disabled:!opacity-40 disabled:cursor-not-allowed disabled:hover:!bg-transparent disabled:hover:!text-[#747474]`}
                    onClick={handleNextGroup}
                    disabled={currentPage === totalPages}
                >
                    <RightOutlined />
                </Button>
            </div>

            {/* Page Input Container */}
            <div className="flex items-center justify-center gap-[0.5rem] text-[#333333]">
                <span className="text-[0.8125rem]">Page</span>
                <Input
                    className="!w-[3.75rem] !h-10 !rounded-[0.625rem] !border-[0.0625rem] !bg-white !border-[#E8E8E8] text-center focus:!border-[#910e11] focus:!shadow-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    type="number"
                    value={inputValue}
                    onPressEnter={handleSubmit}
                    onChange={handleInputChange}
                />
                <span className="text-[0.8125rem]">
                    of {totalPages}
                </span>
            </div>
        </div>
    );
};

export default CustomPagination;